import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ConfirmDialog } from './ConfirmDialog';

/**
 * Imperative, promise-based confirmation — the app-wide replacement for the
 * native `window.confirm()`. NEVER call `window.confirm()`/`window.alert()`/
 * `window.prompt()`; the native dialogs are banned by lint (see
 * `services/web-ui/CLAUDE.md`) because they're unstyled, block the JS thread,
 * and look nothing like the app.
 *
 * Usage — call `confirm()` from any handler and await the boolean:
 *
 *   const confirm = useConfirm();
 *   async function handleDelete() {
 *     if (!(await confirm({
 *       title: 'Delete this run?',
 *       message: "This can't be undone.",
 *       confirmLabel: 'Delete',
 *       destructive: true,
 *     }))) return;
 *     await doDelete();
 *   }
 *
 * It returns a `Promise<boolean>` (true = confirmed, false = cancelled/dismissed),
 * so it drops straight into the `() => Promise<boolean>` callbacks the codebase
 * already uses (e.g. `<Crumbs onBeforeBack={() => confirm({ ... })} />`).
 *
 * `<ConfirmProvider>` renders exactly one shared `<ConfirmDialog>` at the app
 * root, so individual call sites carry no dialog markup or open/onConfirm state.
 */
export type ConfirmOptions = {
  title: string;
  message?: string;
  /** Richer body content rendered below `message` (e.g. an affected-items list). */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red) for delete-type actions. */
  destructive?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Imperative `confirm({...}) → Promise<boolean>`. Must be inside `<ConfirmProvider>`. */
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within a <ConfirmProvider>');
  }
  return confirm;
}

type DialogState = ConfirmOptions & { open: boolean };

const CLOSED: DialogState = { open: false, title: '' };

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>(CLOSED);
  // Holds the in-flight promise's resolver so onConfirm/onCancel can settle it.
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    // A confirm raised while one is already open: resolve the previous as
    // cancelled so its awaiter doesn't hang, then show the new one.
    resolveRef.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ ...options, open: true });
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={state.open}
        title={state.title}
        {...(state.message !== undefined ? { message: state.message } : {})}
        {...(state.confirmLabel !== undefined ? { confirmLabel: state.confirmLabel } : {})}
        {...(state.cancelLabel !== undefined ? { cancelLabel: state.cancelLabel } : {})}
        destructive={state.destructive ?? false}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      >
        {state.children}
      </ConfirmDialog>
    </ConfirmContext.Provider>
  );
}
